"use client";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import zhCN from "../locales/cn";
import enUS from "../locales/en";
import type { LocaleType } from "../locales/cn";
import { isHandyApp, getLangByUaString } from "../utils/device";

// export type Locale = "zh-CN" | "en-US";
export type Locale = string;

interface LocaleContextType {
  locale: Locale;
  messages: LocaleType;
  setLocale: (locale: Locale) => void;
  isLoading: boolean;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const LOCALES: Record<Locale, LocaleType> = {
  "zh-CN": zhCN,
  "en-US": enUS,
};

/**
 * 规范化语言代码，将旧的语言代码转换为新的标准格式
 * @param locale - 原始语言代码
 * @returns 标准化后的语言代码
 */
function normalizeLocale(locale: string): Locale {
  // 向后兼容：将旧的语言代码映射到新的标准格式
  const localeMap: Record<string, Locale> = {
    cn: "zh-CN",
    en: "en-US",
    zh: "zh-CN",
    "zh-cn": "zh-CN",
    "en-us": "en-US",
  };

  const normalized = localeMap[locale.toLowerCase()] || locale;

  // 如果规范化后的语言代码不在支持列表中，返回默认语言
  return LOCALES[normalized] ? normalized : "en-US";
}

/**
 * 根据域名后缀获取默认语言
 * .cn 结尾 -> 中文
 * .com 结尾 -> 英文
 * 其他情况 -> 中文（兜底）
 */
function getLocaleByDomain(): Locale {
  try {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.endsWith(".cn")) {
      return "zh-CN";
    }
    if (hostname.endsWith(".com")) {
      return "en-US";
    }
  } catch (error) {
    console.error("Failed to get locale by domain:", error);
  }
  // 默认中文
  return "zh-CN";
}

/**
 * 获取初始语言
 * 优先级：URL 参数 > Handy App UA > 环境变量 NEXT_PUBLIC_REGION > 域名后缀
 */
function getInitialLocale(): Locale {
  if (typeof window === "undefined") {
    return "zh-CN";
  }

  try {
    // 1. 优先从 URL 参数获取
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get("lang");
    if (urlLang !== null) {
      return normalizeLocale(urlLang);
    }

    // 2. Handy 端：从 UA 中提取语言（BBL-APP-LANGUAGE/xxx）
    if (isHandyApp()) {
      const handyLang = getLangByUaString(navigator.userAgent);
      if (handyLang) {
        return normalizeLocale(handyLang);
      }
    }

    // 3. 根据环境变量 NEXT_PUBLIC_REGION 判断
    const region = process.env.NEXT_PUBLIC_REGION || "";
    if (region.startsWith("OR")) {
      return "en-US";
    }

    // 4. 根据域名后缀判断
    return getLocaleByDomain();
  } catch (error) {
    console.error("Failed to get initial locale:", error);
  }

  // 兜底返回中文
  return "zh-CN";
}

/**
 * 国际化 Provider
 * 管理语言状态和翻译文本
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  // 直接使用 getInitialLocale 作为初始值，避免状态变化
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === "undefined") {
      return "zh-CN";
    }
    return getInitialLocale();
  });
  const [messages, setMessages] = useState<LocaleType>(() => {
    if (typeof window === "undefined") {
      return zhCN;
    }
    const initialLocale = getInitialLocale();
    return LOCALES[initialLocale] || zhCN;
  });
  const [isLoading, setIsLoading] = useState(false); // 改为 false，因为已经同步初始化了

  // 仅用于日志，不再改变状态
  useEffect(() => {
    console.log("[LocaleProvider] Initial locale:", locale);
    console.log("[LocaleProvider] Available locales:", Object.keys(LOCALES));
    console.log("[LocaleProvider] Messages loaded:", !!LOCALES[locale]);
  }, []);

  /**
   * 切换语言
   * @param newLocale - 新的语言代码
   */
  const setLocale = useCallback((newLocale: Locale) => {
    // 规范化语言代码
    const normalizedLocale = normalizeLocale(newLocale);

    // 更新状态
    setLocaleState(normalizedLocale);
    setMessages(LOCALES[normalizedLocale] || zhCN);

    // 更新 URL 参数（不刷新页面）
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", normalizedLocale);
      window.history.replaceState({}, "", url.toString());
    } catch (error) {
      console.error("Failed to update URL:", error);
    }

    // 触发自定义事件，通知其他组件语言已切换
    window.dispatchEvent(
      new CustomEvent("localechange", { detail: { locale: normalizedLocale } }),
    );
  }, []);

  const value: LocaleContextType = {
    locale,
    messages,
    setLocale,
    isLoading,
  };

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

/**
 * 使用国际化 Context
 * @returns LocaleContext
 */
export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
}

/**
 * 获取翻译文本的 Hook
 * @returns 当前语言的翻译对象
 */
export function useTranslations() {
  const { messages } = useLocale();
  return messages;
}

/**
 * 获取当前语言代码
 * @returns 当前语言代码
 */
export function useCurrentLocale() {
  const { locale } = useLocale();
  return locale;
}

/**
 * 语言切换 Hook
 * @returns 切换语言的函数
 */
export function useChangeLocale() {
  const { setLocale } = useLocale();
  return setLocale;
}

/**
 * 支持的所有语言列表
 */
export const ALL_LANG_OPTIONS: Record<Locale, string> = {
  "zh-CN": "简体中文",
  "en-US": "English",
};

/**
 * 所有支持的语言代码
 */
export const AllLangs: Locale[] = ["zh-CN", "en-US"];
